import React from 'react';
import { get, isEmpty } from 'lodash';
import { withRouter } from 'react-router-dom';

// local dependencies
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { DrugSearchService } from 'services/drugSearch';
import { drugSearch as DrugSearchView } from 'ui/views/widgets/drugSearch';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';

// get store
const store = getStore();

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { DrugSearch } from 'ui/containers/DrugSearch';
 *
 * // render
 * <DrugSearch propName='propValue'/>
 */
class _DrugSearch extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            activeStep: 1,
            selectedDrugValue: null,
            drugNdc: '',
            drugFilters: {},
        };

        // create service instance
        this.drugSearchService = new DrugSearchService();
        const memberId = get( this.props.location, 'state.memberId', undefined );
        const patientId = get( this.props.location, 'state.patientId', undefined );

        // Accesses memberId from store to keep behaviour
        // consistent thourghout different routes: Defect 41572
        // const memberId = get( store.getState()[ REDUCER.MEMBER_MANAGEMENT_STATE ], 'memberId', undefined );
        // const patientId = get( store.getState()[ REDUCER.MEMBER_MANAGEMENT_STATE ], 'patientId', undefined );

        // previously if - `memberId` is not present, redirect to the dashboard landing
        // present if - `patientId` is not present, redirect to the dashboard landing
        // else - make service request for the component data
        if(  isEmpty( memberId ) && isEmpty( patientId )  ) {
            this.props.history.goBack();
        } else {
            this.fetchComponentData(); // start component data fetch
        }

        // bind
        this.__bind();

    }

    /**
     * @desc bind events and methods
     */
    __bind() {
        log.debug( 'DrugSearch.__bind()' );

        this.handleDrugSearchQueryChange = this.handleDrugSearchQueryChange.bind( this );
        this.handleDrugSelect = this.handleDrugSelect.bind( this );
        this.handleDrugFilterChange = this.handleDrugFilterChange.bind( this );
        this.handleViewPrice = this.handleViewPrice.bind( this );
        this.handleNavActionClick = this.handleNavActionClick.bind( this );
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'DrugSearch.fetchComponentData()' );

        // cancel ongoing request
        this.drugSearchService.cancel();

        // fetch component data
        const { status, data } = await this.drugSearchService.fetchComponentData( {
            url: this.props.widget.url,
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }

    /**
     * @desc When drug search query changes, display only the first step
     */
    handleDrugSearchQueryChange() {
        log.debug( 'DrugSearch.handleDrugSearchQueryChange()' );

        if( 1 !== this.state.activeStep ) {
            this.setState( {
                activeStep: 1,
                selectedDrugValue: null,
                drugNdc: '',
                drugFilters: {},
            } );
        }
    }

    /**
     * @desc When drug is selected, display next step
     */
    handleDrugSelect( drugValue ) {
        log.debug( 'DrugSearch.handleDrugSelect()' );

        this.setState( {
            activeStep: 2,
            selectedDrugValue: drugValue,
            drugNdc: '',
            drugFilters: {},
        } );
    }

    /**
     * @desc When drug filters changes, display only the first two steps
     */
    handleDrugFilterChange() {
        log.debug( 'DrugSearch.handleDrugFilterChange()' );

        this.setState( {
            activeStep: 2,
            drugNdc: '',
            drugFilters: {},
        } );
    }

    /**
     * @desc When view price button is clicked, execute the callback with filter values.
     */
    handleViewPrice( { drugNdc, drugFilters } = {}  ) {
        log.debug( 'DrugSearch.handleViewPrice()' );

        this.setState( {
            activeStep: 3,
            drugNdc,
            drugFilters,
        } );
    }

    /**
     * @desc When back action is clicked.
     */
    handleNavActionClick( { type, value } ) {
        log.debug( 'DrugSearch.handleNavActionClick()' );

        if( 'headerAction' === type && 'back' === value.name ) {
            this.props.history.goBack();
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.drugSearchService.cancel(); // cancel ongoing requests
    }


    /**
     * @desc Render a React view component
     */
    render() {

        return (
            <DrugSearchView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', {} ) }
                activeStep={ this.state.activeStep }
                selectedDrugValue={ this.state.selectedDrugValue }
                drugNdc={ this.state.drugNdc }
                drugFilters={ this.state.drugFilters }
                onDrugSearchQueryChange={ this.handleDrugSearchQueryChange }
                onDrugSelect={ this.handleDrugSelect }
                onDrugFilterChange={ this.handleDrugFilterChange }
                onViewPrice={ this.handleViewPrice }
                onNavActionClick={ this.handleNavActionClick }
                requestPayload={ {
                    context: {

                        memberId: get( this.props.location, 'state.memberId', undefined ),
                        patientId: get( this.props.location, 'state.patientId', undefined ),
                        
                        // memberId: get( store.getState()[ REDUCER.MEMBER_MANAGEMENT_STATE ], 'memberId', undefined ),
                        // patientId: get( store.getState()[ REDUCER.MEMBER_MANAGEMENT_STATE ], 'patientId', undefined )
                    }
                } }
            />
        );
    }
}

// set display name
_DrugSearch.displayName = '_DrugSearch';

// set default props
_DrugSearch.defaultProps = {};

// prop types
_DrugSearch.propTypes = {};

/*********************************************/

// wrap with router
export const DrugSearch = withRouter( _DrugSearch );
